// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement, DragEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Hand, Grab } from 'lucide-react'

import styles from './AIModelsPage.module.css'
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks'
import {
    selectAiModelsState,
    fetchAiModelsPage,
    refreshAiModels,
} from '../../../store/slices/aiModelsSlice'
import { setAiModelDragData, setAiModelDragging } from '../utils/drag'

const PLACEHOLDER_COUNT = 18
const INTERSECTION_ROOT_MARGIN = '100px'
const BANNER_DISMISSED_KEY = 'knoll_ai_models_banner_dismissed'

function shuffleInPlace<T>(arr: T[]): void {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const temp = arr[i]
        arr[i] = arr[j]
        arr[j] = temp
    }
}

function useShuffledItems<T extends { id: string }>(items: T[]): T[] {
    const shuffledOrderRef = useRef<string[]>([])
    const hasShuffledRef = useRef(false)

    return useMemo(() => {
        if (items.length === 0) {
            shuffledOrderRef.current = []
            hasShuffledRef.current = false
            return []
        }

        if (!hasShuffledRef.current) {
            const shuffled = [...items]
            shuffleInPlace(shuffled)
            shuffledOrderRef.current = shuffled.map((item) => item.id)
            hasShuffledRef.current = true
            return shuffled
        }

        const existingIds = new Set(shuffledOrderRef.current)
        const existingItems = shuffledOrderRef.current
            .map((id) => items.find((item) => item.id === id))
            .filter((item): item is T => item !== undefined)
        const newItems = items.filter((item) => !existingIds.has(item.id))

        shuffledOrderRef.current = existingItems
            .map((item) => item.id)
            .concat(newItems.map((item) => item.id))

        return existingItems.concat(newItems)
    }, [items])
}

function useInitialDataFetch(
    items: unknown[],
    loading: boolean,
    refreshing: boolean
): void {
    const dispatch = useAppDispatch()
    const hasFetchedRef = useRef(false)

    useEffect(() => {
        if (hasFetchedRef.current || loading || refreshing) {
            return
        }

        hasFetchedRef.current = true

        if (items.length === 0) {
            void dispatch(fetchAiModelsPage(1))
        } else {
            void dispatch(refreshAiModels())
        }
    }, [dispatch, items.length, loading, refreshing])
}

function useInfiniteScroll(
    hasMore: boolean,
    loading: boolean,
    refreshing: boolean,
    currentPage: number
): React.RefObject<HTMLDivElement | null> {
    const dispatch = useAppDispatch()
    const sentinelRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        const element = sentinelRef.current
        if (!element) {
            return
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const [firstEntry] = entries
                if (
                    firstEntry.isIntersecting &&
                    hasMore &&
                    !loading &&
                    !refreshing
                ) {
                    const nextPage = currentPage + 1
                    void dispatch(fetchAiModelsPage(nextPage))
                }
            },
            { rootMargin: INTERSECTION_ROOT_MARGIN }
        )

        observer.observe(element)

        return () => {
            observer.disconnect()
        }
    }, [dispatch, hasMore, loading, refreshing, currentPage])

    return sentinelRef
}

function handleImageDragStart(
    event: DragEvent<HTMLImageElement>,
    imageUrl: string
): void {
    const resolvedUrl =
        imageUrl || event.currentTarget.currentSrc || event.currentTarget.src
    if (!resolvedUrl) {
        return
    }

    setAiModelDragging(true)
    try {
        setAiModelDragData(event.dataTransfer, resolvedUrl)
    } catch {
        // Ignore drag errors
    }
}

function handleImageDragEnd(): void {
    setAiModelDragging(false)
}

function handleImageError(event: React.SyntheticEvent<HTMLImageElement>): void {
    const target = event.currentTarget as HTMLImageElement
    target.style.visibility = 'hidden'
}

interface ModelCardProps {
    imageUrl: string
    onDragStart?: (event: DragEvent<HTMLImageElement>, imageUrl: string) => void
    onDragEnd?: () => void
}

function ModelCard({
    imageUrl,
    onDragStart,
    onDragEnd,
}: ModelCardProps): ReactElement {
    const handleDragStart = useCallback((event: DragEvent<HTMLImageElement>) => {
        if (onDragStart) {
            onDragStart(event, imageUrl)
            return
        }
        handleImageDragStart(event, imageUrl)
    }, [imageUrl, onDragStart])

    const handleDragEnd = useCallback(() => {
        if (onDragEnd) {
            onDragEnd()
            return
        }
        handleImageDragEnd()
    }, [onDragEnd])

    return (
        <div className={styles.modelCard}>
            <img
                className={styles.modelImg}
                src={imageUrl}
                alt=""
                loading="lazy"
                width={300}
                height={400}
                draggable
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onError={handleImageError}
            />
        </div>
    )
}

function PlaceholderCards(): ReactElement {
    return (
        <>
            {Array.from({ length: PLACEHOLDER_COUNT }, (_, index) => (
                <div
                    key={`placeholder-${index}`}
                    className={styles.modelCardPlaceholder}
                />
            ))}
        </>
    )
}

function ErrorMessage({ message }: { message: string }): ReactElement {
    return <div className={styles.modelsError}>{message}</div>
}

interface BannerProps {
    onDismiss: () => void
}

function Banner({ onDismiss }: BannerProps): ReactElement {
    const { t } = useTranslation('canvas')

    return (
        <div className={styles.banner} role="status">
            <div className={styles.bannerContent}>
                <div className={styles.bannerText}>
                    {t('backstage.dragAndDropHint')}
                </div>
                <div
                    className={styles.dragAnimationContainer}
                    aria-hidden="true"
                >
                    <div className={styles.dragTarget} />
                    <div className={styles.dragSource} />
                    <div className={styles.dragCursorDefault}>
                        <Hand size={24} strokeWidth={1.5}/>
                    </div>
                    <div className={styles.dragCursorGrab}>
                        <Grab size={24} strokeWidth={1.5} />
                    </div>
                </div>
            </div>
            <button
                className={styles.bannerDismiss}
                type="button"
                onClick={onDismiss}
                aria-label={t('backstage.dismissNotice')}
            >
                <X className={styles.bannerDismissIcon} />
            </button>
        </div>
    )
}

export default function AIModelsPage(): ReactElement {
    const { items, total, currentPage, loading, refreshing, error } =
        useAppSelector(selectAiModelsState)
    const [isBannerDismissed, setIsBannerDismissed] = useState(
        () => localStorage.getItem(BANNER_DISMISSED_KEY) === 'true'
    )

    const handleBannerDismiss = (): void => {
        localStorage.setItem(BANNER_DISMISSED_KEY, 'true')
        setIsBannerDismissed(true)
    }

    const shuffledItems = useShuffledItems(items)
    const sentinelRef = useInfiniteScroll(
        shuffledItems.length < total,
        loading,
        refreshing,
        currentPage
    )

    useInitialDataFetch(items, loading, refreshing)

    const handleModelDragStart = useCallback(
        (event: DragEvent<HTMLImageElement>, imageUrl: string) => {
            handleImageDragStart(event, imageUrl)
        },
        []
    )

    const handleModelDragEnd = useCallback(() => {
        handleImageDragEnd()
    }, [])

    const hasMore = shuffledItems.length < total
    const showPlaceholders = shuffledItems.length === 0
    const showErrorOnly = error !== null && items.length === 0
    const showErrorBanner = error !== null && items.length > 0

    if (showErrorOnly) {
        return (
            <div className={styles.container}>
                {!isBannerDismissed && (
                    <Banner onDismiss={handleBannerDismiss} />
                )}
                <div className={styles.modelsGrid}>
                    <ErrorMessage message={error} />
                </div>
            </div>
        )
    }

    return (
        <div className={styles.container}>
            {!isBannerDismissed && (
                <Banner onDismiss={handleBannerDismiss} />
            )}
            <div className={styles.modelsGrid}>
                {showPlaceholders ? (
                    <PlaceholderCards />
                ) : (
                    shuffledItems.map((model) => (
                        <ModelCard
                            key={model.id}
                            imageUrl={model.headshot_url ?? ''}
                            onDragStart={handleModelDragStart}
                            onDragEnd={handleModelDragEnd}
                        />
                    ))
                )}

                {(loading || refreshing || hasMore) && (
                    <div
                        ref={sentinelRef}
                        className={styles.modelsSentinel}
                        aria-hidden="true"
                    />
                )}

                {showErrorBanner && <ErrorMessage message={error} />}
            </div>
        </div>
    )
}

